/* ==========================================================================
   SMOON — page « Notre technologie » (page marque #13)
   --------------------------------------------------------------------------
   1. Apparitions au défilement (.nt-reveal → .is-in)
   2. Pile des matières : scène épinglée, chaque matière monte depuis le bas
      et se loge sous la précédente, son texte se place sous la pile, la
      vidéo de la matière posée se lance, puis la pile complète passe en
      action avec ses légendes. Le défilement déclenche les étapes, il ne
      les pilote pas : un geste fait avancer d'une matière (son élan est
      ignoré), chaque passage se joue en entier et chaque matière reste à
      l'écran un temps minimum. À l'arrêt, un aimant ramène la page sur la
      matière affichée.
   3. Filets de la section « sans couture ».

   Chargé en module par les six sections : s'exécute une fois, réinitialise
   une section rechargée dans l'éditeur de thème.
   ========================================================================== */

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const hasIO = 'IntersectionObserver' in window;
const hasRO = 'ResizeObserver' in window;

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const seg = (t, a, b) => clamp((t - a) / (b - a), 0, 1);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeIn = (t) => t * t * t;
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function slowNetwork() {
  const c = navigator.connection;
  return !!(c && (c.saveData || /(^|[^3-5])2g/.test(c.effectiveType || '')));
}

/* ------------------------------------------------------- 1. Apparitions */

let revealIO = null;

function reveals(root) {
  const els = root.querySelectorAll('.nt-reveal:not(.is-in)');
  const sections = root.querySelectorAll('[data-nt-seam-section]:not(.is-in)');
  if (!hasIO || reduced.matches) {
    els.forEach((el) => el.classList.add('is-in'));
    sections.forEach((el) => el.classList.add('is-in'));
    return;
  }
  if (!revealIO) {
    revealIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealIO.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
  }
  els.forEach((el) => revealIO.observe(el));
  sections.forEach((el) => revealIO.observe(el));
}

/* ---------------------------------------------------- 2. Pile des matières */

/* Masque de la vidéo, fabriqué au vol dans un canvas (aucun fichier en plus)
   depuis la silhouette de l'image « en action » : étiré vers le haut, parce
   que la matière flotte de quelques pixels dans la vidéo, puis rogné sur les
   côtés et en bas, où le bord de la vidéo mêle la matière au fond gris.
   Érosion par intersection de copies décalées. En cas d'échec, le masque posé
   dans le Liquid reste en place. */
async function erodedMask(src, radius = 1.6, lift = 5, width = 480) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.decoding = 'async';
  img.src = src;
  await img.decode();
  const w = width;
  const h = Math.round((w * img.naturalHeight) / img.naturalWidth);
  const base = document.createElement('canvas');
  base.width = w;
  base.height = h;
  base.getContext('2d').drawImage(img, 0, 0, w, h);
  /* La matière flotte vers le haut dans la vidéo (jusqu'à 8 px sur 852) :
     le masque suit ce mouvement en s'étirant vers le haut… */
  const up = document.createElement('canvas');
  up.width = w;
  up.height = h;
  const uctx = up.getContext('2d');
  for (let k = 0; k <= lift; k++) uctx.drawImage(base, 0, -k);
  /* …puis se rogne sur les côtés et en bas, où le bord de la vidéo mêle la
     matière au fond gris. */
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  ctx.drawImage(up, 0, 0);
  ctx.globalCompositeOperation = 'destination-in';
  [[radius, 0], [-radius, 0], [0, -radius]].forEach(([dx, dy]) => ctx.drawImage(up, dx, dy));
  return out.toDataURL('image/png');
}

/* Constantes des rendus 3D (PNG 1920 × 1080, même cadrage que les vidéos). */
const RATIO = 9 / 16; /* hauteur / largeur du cadre */
const GAP = 0.278; /* écart entre deux matières posées, en hauteur de cadre (mesuré sur 3_Couches_1.png) */
const TOP = 0.19; /* haut visible d'une matière dans son cadre */
const BOT = 0.83; /* bas visible */
const TIPS = [
  [0.866, 0.34],
  [0.861, 0.306],
  [0.839, 0.263],
  [0.84, 0.26],
]; /* pointe droite de chaque matière (x, y dans le cadre) : départ des légendes */

/* Chronologie, en « temps de passage » : un passage d'une matière à la
   suivante vaut 1 et dure STEP_TIME secondes. */
const SEG = { dwell: 0.06, move: 1, final: 1.25, hold: 0.02 };
const STEP_TIME = 0.95;
const PAUSE = 380; /* arrêt (ms) sur une matière traversée quand plusieurs étapes s'enchaînent */
/* Défilement par étape et laissé à la pile finale, en hauteurs d'écran
   (× le réglage « Longueur du défilement »). */
const ZONE = 0.7;
/* Temps d'affichage minimal d'une matière avant de passer à la suivante
   (ms, après son arrivée) : le temps de voir la vidéo agir. */
const MIN_VIEW = 1600;
const HOLD = 0.6;

const instances = new Map();

class Stack {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.nt-stack__stage');
    this.scene = root.querySelector('[data-nt-scene]');
    this.textsBox = root.querySelector('[data-nt-texts]');
    this.layers = Array.from(root.querySelectorAll('[data-nt-layer]'));
    this.steps = Array.from(root.querySelectorAll('[data-nt-step]'));
    this.finalEl = root.querySelector('[data-nt-final]');
    this.shadow = root.querySelector('[data-nt-shadow]');
    this.callouts = Array.from(root.querySelectorAll('[data-nt-callout]'));
    this.n = this.layers.length;
    this.len = (parseFloat(root.dataset.length) || 100) / 100;
    this.wantVideo = root.dataset.video === '1' && !slowNetwork();
    this.acts = this.layers.map((l) => l.querySelector('.nt-layer__act'));
    this.videos = this.layers.map((l) => l.querySelector('.nt-layer__video'));
    this.vs = this.videos.map(() => ({ loaded: false, failed: false, off: -Infinity, pauseT: 0, stallT: 0 }));
    this.activeVideo = -1;
    this.cu = null;
    this.tu = 0;
    this.raf = 0;
    this.last = 0;
    this.near = !hasIO;
    this.listening = false;
    this.target = 0;
    this.s = 0;
    this.snapT = 0;
    this.touching = false;
    this.locked = false;
    this.gate = 0;
    this.gateBack = 0;

    this.tick = this.tick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onTouch = this.onTouch.bind(this);

    this.timeline();
    root.classList.add('is-live');
    this.measure();
    this.readScroll(true);
    this.cu = this.tu;
    this.render(this.cu);

    root.addEventListener('click', this.onClick);
    root.addEventListener('focusin', this.onFocus);
    document.addEventListener('visibilitychange', this.onVisibility);
    if (hasRO) {
      this.ro = new ResizeObserver(() => this.onResize());
      this.ro.observe(this.stage);
      this.steps.forEach((s) => this.ro.observe(s));
    } else {
      window.addEventListener('resize', this.onResize);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.onResize());

    if (hasIO) {
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.near = entry.isIntersecting;
            this.listen(this.near);
            if (this.near) this.warm();
            this.syncVideo();
          });
        },
        { rootMargin: '80% 0px 80% 0px' }
      );
      this.io.observe(root);
    } else {
      this.listen(true);
      this.warm();
    }
  }

  destroy() {
    this.listen(false);
    cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.snapT);
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('focusin', this.onFocus);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.videos.forEach((v) => v && v.pause());
  }

  timeline() {
    const segs = [];
    let t = 0;
    for (let i = 0; i < this.n; i++) {
      if (i > 0) {
        segs.push({ k: 'move', i, a: t, b: t + SEG.move });
        t += SEG.move;
      }
      segs.push({ k: 'dwell', i, a: t, b: t + SEG.dwell });
      t += SEG.dwell;
    }
    segs.push({ k: 'final', a: t, b: t + SEG.final });
    t += SEG.final;
    segs.push({ k: 'hold', a: t, b: t + SEG.hold });
    t += SEG.hold;
    this.segs = segs;
    this.total = t;
    /* Position de repos de chaque étape : milieu de chaque matière posée,
       puis fin de la pile complète. */
    this.rests = segs.filter((s) => s.k === 'dwell').map((s) => (s.a + s.b) / 2);
    this.rests.push(segs.find((s) => s.k === 'final').b);
  }

  /* Géométrie : largeur de cadre W telle que la pile complète tienne entre
     la marge haute et la zone de texte, bornée par la largeur d'écran. */
  measure() {
    const SW = this.stage.clientWidth;
    const SH = this.stage.clientHeight;
    if (!SW || !SH) return;
    const mobile = SW < 750;
    const padT = mobile ? 62 : Math.max(76, SH * 0.1);
    const padB = mobile ? 22 : Math.max(34, SH * 0.055);
    const gapT = mobile ? 10 : 20;
    let textH = 0;
    this.steps.forEach((s) => {
      textH = Math.max(textH, s.offsetHeight);
    });
    const areaTop = padT;
    const areaH = Math.max(110, SH - padB - textH - gapT - areaTop);
    const unitV = RATIO * ((this.n - 1) * GAP + (BOT - TOP));
    let W = areaH / unitV;
    W = Math.min(W, mobile ? SW * 1.16 : SW < 990 ? SW * 0.84 : Math.min(SW * 0.56, 780));
    W = Math.max(W, Math.min(SW * 0.62, 230));
    this.g = { SW, SH, mobile, padT, padB, gapT, textH, areaTop, areaH, W, H: W * RATIO };

    this.scene.style.setProperty('--nt-w', W.toFixed(1) + 'px');
    this.scene.style.setProperty('--nt-sw', (0.64 * W).toFixed(1) + 'px');
    this.textsBox.style.setProperty('--nt-text-top', (SH - padB - textH).toFixed(1) + 'px');
    /* Piste : une zone de défilement par étape (matières puis pile complète),
       plus une marge pour la pile finale avant que la scène ne parte. */
    this.zone = SH * ZONE * this.len;
    this.root.style.setProperty('--nt-track', Math.round(SH * (1 + HOLD) + this.n * this.zone) + 'px');

    this.poses = [];
    for (let i = 0; i < this.n; i++) this.poses.push(this.dwellPose(i));
    this.fin = this.finalPose();
  }

  dwellPose(k) {
    const g = this.g;
    const H = g.H;
    const s = GAP * H;
    const V = k * s + (BOT - TOP) * H;
    const top0 = g.areaTop + (g.areaH - V) / 2 - TOP * H;
    const x = g.SW / 2 - g.W / 2;
    const L = [];
    for (let j = 0; j <= k; j++) L.push({ x, y: top0 + j * s, sc: 1 });
    return { L, sh: { cx: g.SW / 2, y: top0 + k * s + (BOT + 0.1) * H, w: 0.64 * g.W } };
  }

  finalPose() {
    const g = this.g;
    const n = this.n;
    const k = n - 1;
    const fh = this.finalEl ? this.finalEl.offsetHeight : 0;
    const areaTop = g.padT;
    const areaBot = g.SH - g.padB - (fh ? fh + g.gapT * 2 : 0);
    const areaH = Math.max(110, areaBot - areaTop);
    const unitV = RATIO * (k * GAP + (BOT - TOP));

    let labelW = 0;
    this.callouts.forEach((c) => {
      const lab = c.querySelector('.nt-callout__label');
      labelW = Math.max(labelW, lab ? lab.offsetWidth : 0);
    });
    const lineMin = g.mobile ? 10 : 40;
    const gapL = g.mobile ? 6 : 14;
    const side = this.callouts.length ? lineMin + gapL + labelW : 0;
    const tips = this.layers.map((_, j) => TIPS[Math.min(j, TIPS.length - 1)]);
    const maxTip = Math.max(...tips.map((t) => t[0]));
    /* Sur mobile, la pointe gauche de la pile peut frôler le bord : la
       légende garde sa place à droite, la pile gagne en taille. */
    const margin = g.mobile ? 4 : 56;
    const wMax = (g.SW - 2 * margin - side) / (0.37 + (maxTip - 0.5));
    const Wf = Math.max(160, Math.min(areaH / unitV, wMax, g.W * (g.mobile ? 1.04 : 1.32), 920));
    const H = Wf * RATIO;
    const s = GAP * H;
    const V = k * s + (BOT - TOP) * H;
    const top0 = areaTop + (areaH - V) / 2 - TOP * H;
    const left = 0.37 * Wf;
    const right = (maxTip - 0.5) * Wf + side;
    const cx = g.SW / 2 + (left - right) / 2;
    const sc = Wf / g.W;
    const L = [];
    for (let j = 0; j < n; j++) L.push({ x: cx - g.W / 2, y: top0 + j * s - (g.H - H) / 2, sc });

    const colX = cx + (maxTip - 0.5) * Wf + lineMin;
    const calls = this.callouts.map((c, j) => {
      const t = tips[j] || tips[tips.length - 1];
      const x = cx + (t[0] - 0.5) * Wf + 6;
      const y = top0 + j * s + t[1] * H - c.offsetHeight / 2;
      return { x, y, len: Math.max(8, colX - x) };
    });
    if (this.finalEl) this.finalTop = areaBot + g.gapT * 2;
    return { L, sh: { cx, y: top0 + k * s + (BOT + 0.1) * H, w: 0.64 * Wf }, calls };
  }

  /* Seuil de déclenchement : un cran de molette ou un petit geste suffit. */
  hyst() {
    return Math.min(60, 0.15 * this.zone);
  }

  /* Position de défilement → étape visée. Avant l'épinglage, l'entrée de la
     première matière suit le défilement ; ensuite, on passe d'étape en étape
     dès que le seuil est franchi (avec hystérésis, pour ne pas osciller). */
  readScroll(reset) {
    const SH = (this.g && this.g.SH) || window.innerHeight;
    const s = -this.root.getBoundingClientRect().top;
    const dir = s - this.s;
    this.s = s;
    /* Tolérance de 2 px : selon le navigateur, l'épinglage tombe à une
       fraction de pixel près. */
    if (s < -2) {
      this.target = 0;
      this.tu = s / SH;
      return;
    }
    const last = this.n;
    const T = this.hyst();
    const now = performance.now();
    if (reset || Math.abs(dir) > 1.2 * this.zone) {
      /* Saut (chargement, barre de défilement, touche Fin) : on se cale. */
      this.target = clamp(Math.round(s / this.zone), 0, last);
      this.locked = false;
    } else if (s > last * this.zone + T) {
      /* La visiteuse quitte la scène par le bas : pile complète. */
      this.target = last;
    } else if (!this.locked) {
      /* Un geste = une matière. Seuls les seuils du sens de défilement
         comptent, et la suivante attend que la précédente ait été vue. */
      if (dir > 0 && this.target < last && s > this.target * this.zone + T && now >= this.gate) {
        this.target++;
        this.lockStep(now);
      } else if (dir < 0 && this.target > 0 && s < this.target * this.zone - T && now >= this.gateBack) {
        this.target--;
        this.lockStep(now);
      }
    }
    this.tu = this.rests[this.target];
  }

  /* Après un pas : le reste du geste (élan compris) est ignoré jusqu'à
     l'arrêt du défilement, et le pas suivant attend la fin du passage plus
     MIN_VIEW (un retour en arrière est plus vite permis). */
  lockStep(now) {
    this.locked = true;
    this.gate = now + STEP_TIME * 1000 + MIN_VIEW;
    this.gateBack = now + STEP_TIME * 600;
  }

  segAt(u) {
    for (let i = 0; i < this.segs.length; i++) if (u < this.segs[i].b) return this.segs[i];
    return this.segs[this.segs.length - 1];
  }

  render(u) {
    if (!this.g) return;
    const g = this.g;
    const n = this.n;
    const L = new Array(n);
    const acts = new Array(n).fill(0);
    const steps = this.steps.map(() => ({ op: 0, dy: 18 }));
    let shadow = null;
    let calls = null;
    let finalOp = 0;
    let vid = -1;

    const below = (j, pose) => {
      const p = pose.L[j] || pose.L[pose.L.length - 1];
      return { x: p.x, y: g.SH + 0.12 * g.H, sc: 0.9, rot: 4, op: 0 };
    };
    const mix = (a, b, e) => ({ x: lerp(a.x, b.x, e), y: lerp(a.y, b.y, e), sc: lerp(a.sc, b.sc, e), rot: 0, op: 1 });
    /* Avec une vidéo, c'est elle qui montre la matière en action : l'image
       « en action » ne sert qu'au relais (pas de vidéo) et à la pile finale. */
    const vidOk = (j) => !!(this.wantVideo && this.videos[j] && !this.vs[j].failed);
    const mixSh = (a, b, e) => ({ cx: lerp(a.cx, b.cx, e), y: lerp(a.y, b.y, e), w: lerp(a.w, b.w, e), op: 1 });

    if (u < 0) {
      /* Approche : la scène entre à l'écran, la première matière s'élève. */
      const P = this.poses[0];
      const e = easeOut(seg(u, -0.9, -0.18));
      const p0 = P.L[0];
      L[0] = { x: p0.x, y: p0.y + 90 * (1 - e), sc: 0.93 + 0.07 * e, rot: 0, op: e };
      for (let j = 1; j < n; j++) L[j] = below(j, this.poses[j]);
      const t = seg(u, -0.5, -0.08);
      steps[0] = { op: t, dy: 18 * (1 - easeOut(t)) };
      shadow = { ...P.sh, op: e };
    } else {
      const sg = this.segAt(u);
      const m = seg(u, sg.a, sg.b);
      if (sg.k === 'dwell') {
        const i = sg.i;
        const P = this.poses[i];
        for (let j = 0; j < n; j++) L[j] = j <= i ? { ...P.L[j], rot: 0, op: 1 } : below(j, this.poses[j]);
        steps[i] = { op: 1, dy: 0 };
        acts[i] = vidOk(i) ? 0 : 1;
        shadow = { ...P.sh, op: 1 };
        vid = i;
      } else if (sg.k === 'move') {
        /* La matière i monte et se loge sous la pile ; le texte précédent
           s'efface avant qu'elle ne le traverse, le suivant apparaît dessous. */
        const i = sg.i;
        const A = this.poses[i - 1];
        const B = this.poses[i];
        const e = easeInOut(m);
        for (let j = 0; j < n; j++) {
          if (j < i) L[j] = mix(A.L[j], B.L[j], e);
          else if (j > i) L[j] = below(j, this.poses[j]);
        }
        const from = below(i, B);
        const to = B.L[i];
        L[i] = {
          x: lerp(from.x, to.x, e),
          y: lerp(from.y, to.y, e),
          sc: lerp(from.sc, 1, e),
          rot: lerp(from.rot, 0, easeOut(m)),
          op: seg(m, 0, 0.1),
        };
        const out = seg(m, 0.04, 0.34);
        const inn = seg(m, 0.7, 0.97);
        steps[i - 1] = { op: 1 - out, dy: -22 * easeIn(out) };
        steps[i] = { op: inn, dy: 22 * (1 - easeOut(inn)) };
        acts[i - 1] = 1 - seg(m, 0, 0.3);
        if (!vidOk(i)) acts[i] = seg(m, 0.75, 1);
        shadow = mixSh(A.sh, B.sh, e);
        if (m < 0.12) vid = i - 1;
        else if (m > 0.8) vid = i;
      } else {
        /* Pile complète : elle se recentre, passe en action, les légendes
           se tracent. */
        const f = sg.k === 'final' ? m : 1;
        const A = this.poses[n - 1];
        const F = this.fin;
        const e = easeInOut(seg(f, 0.06, 0.8));
        for (let j = 0; j < n; j++) L[j] = mix(A.L[j], F.L[j], e);
        const out = seg(f, 0, 0.26);
        steps[n - 1] = { op: 1 - out, dy: -18 * easeIn(out) };
        for (let j = 0; j < n; j++) acts[j] = j === n - 1 ? 1 : seg(f, 0.3, 0.72);
        shadow = mixSh(A.sh, F.sh, e);
        calls = F.calls.map((c, j) => ({
          ...c,
          draw: easeOut(seg(f, 0.5 + 0.07 * j, 0.8 + 0.07 * j)),
          op: seg(f, 0.52 + 0.07 * j, 0.78 + 0.07 * j),
        }));
        finalOp = seg(f, 0.62, 0.92);
        if (sg.k === 'final' && m < 0.1) vid = n - 1;
      }
    }

    /* Écriture : transformations et opacités seulement (compositeur). */
    for (let j = 0; j < n; j++) {
      const p = L[j];
      const el = this.layers[j];
      el.style.transform = `translate3d(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px, 0) rotate(${(p.rot || 0).toFixed(3)}deg) scale(${p.sc.toFixed(4)})`;
      el.style.opacity = p.op.toFixed(3);
      if (this.acts[j]) this.acts[j].style.opacity = acts[j].toFixed(3);
    }
    this.steps.forEach((el, i) => {
      const st = steps[i];
      el.style.opacity = st.op.toFixed(3);
      el.style.transform = `translate3d(0, ${st.dy.toFixed(2)}px, 0)`;
      el.classList.toggle('is-on', st.op > 0.5);
    });
    if (this.finalEl) {
      this.finalEl.style.top = ((this.finalTop || 0) - (g.SH - g.padB - g.textH)).toFixed(1) + 'px';
      this.finalEl.style.opacity = finalOp.toFixed(3);
      this.finalEl.style.transform = `translate3d(0, ${(14 * (1 - finalOp)).toFixed(2)}px, 0)`;
    }
    if (this.shadow && shadow) {
      const base = 0.64 * g.W;
      this.shadow.style.transform = `translate3d(${(shadow.cx - base / 2).toFixed(2)}px, ${shadow.y.toFixed(2)}px, 0) scale(${(shadow.w / base).toFixed(4)})`;
      this.shadow.style.opacity = shadow.op.toFixed(3);
    }
    this.callouts.forEach((el, j) => {
      const c = calls && calls[j];
      if (!c) {
        el.style.opacity = '0';
        el.style.setProperty('--nt-draw', '0');
        return;
      }
      el.style.transform = `translate3d(${c.x.toFixed(2)}px, ${c.y.toFixed(2)}px, 0)`;
      el.style.setProperty('--nt-len', c.len.toFixed(1) + 'px');
      el.style.setProperty('--nt-draw', c.draw.toFixed(3));
      el.style.opacity = c.op.toFixed(3);
    });

    /* Plusieurs étapes enchaînées d'un coup : pas de vidéo au passage. */
    this.setVideo(this.transit ? -1 : vid);
  }

  /* ----------------------------------------------------------- vidéos */

  setVideo(idx) {
    if (idx === this.activeVideo) return;
    const prev = this.activeVideo;
    this.activeVideo = idx;
    this.layers.forEach((layer, j) => {
      layer.classList.toggle('is-video', j === idx);
      if (j !== idx) layer.classList.remove('is-covered');
    });
    if (prev >= 0) this.stopVideo(prev);
    this.syncVideo();
    /* La suivante se prépare pendant que celle-ci joue. */
    if (idx >= 0 && idx + 1 < this.n) this.load(idx + 1);
  }

  syncVideo() {
    const j = this.activeVideo;
    this.videos.forEach((v, i) => {
      if (v && i !== j && !v.paused) this.stopVideo(i);
    });
    if (j < 0) return;
    const v = this.videos[j];
    const st = this.vs[j];
    if (!v || !this.wantVideo || st.failed) return;
    if (!this.near || document.hidden) {
      v.pause();
      return;
    }
    if (!this.load(j)) return;
    clearTimeout(st.pauseT);
    if (performance.now() - st.off > 1500) {
      /* Départ sur l'action (eau, taches, vapeur), réglé par matière. */
      const t0 = parseFloat(v.dataset.ntStart) || 0;
      if (v.readyState >= 1) {
        try {
          v.currentTime = t0;
        } catch (e) {
          /* rien */
        }
      } else if (t0) {
        v.addEventListener('loadedmetadata', () => { v.currentTime = t0; }, { once: true });
      }
    }
    /* Revenue sur une matière avant la mise en pause : la vidéo joue encore,
       aucun « playing » ne repartira. */
    if (!v.paused && v.classList.contains('is-playing')) this.layers[j].classList.add('is-covered');
    const p = v.play();
    if (p && p.catch) {
      p.catch((err) => {
        /* pause() pendant un play() en attente : AbortError, pas un refus. */
        if (!err || err.name !== 'AbortError') this.fail(j);
      });
    }
  }

  stopVideo(j) {
    const v = this.videos[j];
    const st = this.vs[j];
    if (!v) return;
    st.off = performance.now();
    clearTimeout(st.pauseT);
    st.pauseT = setTimeout(() => {
      if (this.activeVideo !== j) v.pause();
    }, 650);
  }

  /* Source posée au premier besoin : la plus petite définition MP4 qui
     couvre le cadre (densité plafonnée à 2). Une lecture qui cale six
     secondes, ou qui est refusée, rend la main à l'image « en action ». */
  load(j) {
    const v = this.videos[j];
    const st = this.vs[j];
    if (!v || !this.wantVideo || st.failed || !this.near) return false;
    if (st.loaded) return true;
    let list = [];
    try {
      list = JSON.parse(v.dataset.ntSources || '[]');
    } catch (e) {
      list = [];
    }
    if (!list.length) {
      st.failed = true;
      return false;
    }
    const need = this.g.H * Math.min(window.devicePixelRatio || 1, 2);
    list.sort((a, b) => a.h - b.h);
    const pick = list.find((x) => x.h >= need) || list[list.length - 1];
    const stalled = () => {
      clearTimeout(st.stallT);
      st.stallT = setTimeout(() => this.fail(j), 6000);
    };
    v.addEventListener('playing', () => {
      clearTimeout(st.stallT);
      v.classList.add('is-playing');
      if (this.activeVideo === j) this.layers[j].classList.add('is-covered');
    });
    v.addEventListener('pause', () => {
      clearTimeout(st.stallT);
      v.classList.remove('is-playing');
      this.layers[j].classList.remove('is-covered');
    });
    v.addEventListener('waiting', stalled);
    v.addEventListener('stalled', stalled);
    v.addEventListener('error', () => this.fail(j));
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.src = pick.u;
    v.load();
    st.loaded = true;
    return true;
  }

  fail(j) {
    const v = this.videos[j];
    const st = this.vs[j];
    st.failed = true;
    clearTimeout(st.stallT);
    this.layers[j].classList.remove('is-covered');
    if (!v) return;
    v.classList.remove('is-playing');
    try {
      v.pause();
    } catch (e) {
      /* rien */
    }
  }

  /* --------------------------------------------------------- défilement */

  warm() {
    /* Les images des matières se chargent dès que la scène approche. */
    this.root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
    if (this.masked || !this.wantVideo) return;
    this.masked = true;
    this.videos.forEach((v, j) => {
      if (!v) return;
      const pic = this.acts[j] || this.layers[j].querySelector('.nt-layer__img');
      const src = pic && (pic.currentSrc || pic.src);
      if (!src) return;
      erodedMask(src)
        .then((url) => {
          v.style.webkitMaskImage = `url("${url}")`;
          v.style.maskImage = `url("${url}")`;
          v.style.webkitMaskSize = '100% 100%';
          v.style.maskSize = '100% 100%';
        })
        .catch(() => {
          /* masque du Liquid conservé */
        });
    });
  }

  listen(on) {
    if (on === this.listening) return;
    this.listening = on;
    const m = on ? 'addEventListener' : 'removeEventListener';
    window[m]('scroll', this.onScroll, { passive: true });
    window[m]('touchstart', this.onTouch, { passive: true });
    window[m]('touchend', this.onTouch, { passive: true });
    window[m]('touchcancel', this.onTouch, { passive: true });
    if (on) this.onScroll();
  }

  onScroll() {
    this.readScroll(false);
    this.armSnap();
    if (!this.raf) this.raf = requestAnimationFrame(this.tick);
  }

  onTouch(e) {
    this.touching = e.type === 'touchstart';
    if (!this.touching) this.armSnap();
  }

  /* Aimant : défilement arrêté entre deux étapes (doigt levé, élan fini),
     la page glisse jusqu'à l'étape en cours. Rien hors de la scène, ni quand
     on la quitte par le bas. */
  armSnap() {
    clearTimeout(this.snapT);
    this.snapT = setTimeout(() => this.snap(), 180);
  }

  snap() {
    if (this.touching || !this.g) return;
    /* Défilement arrêté : fin du geste, le pas suivant redevient possible. */
    this.locked = false;
    if (!this.near) return;
    const s = this.s;
    if (s < -2 || s > this.n * this.zone + this.hyst()) return;
    const want = this.target * this.zone;
    if (Math.abs(s - want) < 4) return;
    const top = this.root.getBoundingClientRect().top + window.scrollY + want;
    window.scrollTo({ top: Math.round(top), behavior: 'smooth' });
  }

  /* L'animation avance dans le temps vers l'étape visée : un passage dure
     STEP_TIME, et accélère s'il y a plusieurs étapes à rattraper. Avant
     l'épinglage, elle suit le défilement directement. */
  tick(now) {
    this.raf = 0;
    const dt = this.last ? Math.min(0.064, (now - this.last) / 1000) : 1 / 60;
    this.last = now;
    if (this.cu === null) this.cu = this.tu;
    const d = this.tu - this.cu;
    const start = this.rests[0];
    if (this.cu <= start && this.tu <= start) {
      this.cu = this.tu;
      this.holdUntil = 0;
    } else if (!(this.holdUntil && now < this.holdUntil)) {
      this.holdUntil = 0;
      const left = Math.abs(d);
      /* Hors de la zone épinglée (scène qui entre ou qui part), la
         visiteuse ne la regarde plus en face : rattrapage rapide. */
      const away = this.s < -2 || this.s > this.n * this.zone + this.hyst();
      const v = ((1 + 0.3 * Math.max(0, left - 1)) / STEP_TIME) * (away ? 3 : 1);
      let next = left <= v * dt ? this.tu : this.cu + Math.sign(d) * v * dt;
      /* Étape traversée en chemin : bref arrêt, le temps de lire son nom. */
      const pass = away ? undefined : this.rests.find((r) => r !== this.tu && (d > 0 ? r > this.cu + 1e-6 && r < next : r < this.cu - 1e-6 && r > next));
      if (pass !== undefined) {
        next = pass;
        this.holdUntil = now + PAUSE;
      }
      this.cu = next;
    }
    this.transit = Math.abs(this.tu - this.cu) > 1.1;
    this.render(this.cu);
    if (this.cu !== this.tu) this.raf = requestAnimationFrame(this.tick);
    else this.last = 0;
  }

  onResize() {
    if (this.resizing) return;
    this.resizing = true;
    requestAnimationFrame(() => {
      this.resizing = false;
      this.measure();
      this.readScroll(true);
      this.cu = this.tu;
      this.render(this.cu);
    });
  }

  onVisibility() {
    this.syncVideo();
  }

  /* Onglets « Matière 1 - 2 - 3 » et sélection d'un bloc dans l'éditeur :
     défilement jusqu'au moment où la matière est posée. */
  goTo(i) {
    if (!this.g || i < 0 || i >= this.n) return;
    this.target = i;
    this.tu = this.rests[i];
    this.locked = true;
    if (!this.raf) this.raf = requestAnimationFrame(this.tick);
    const top = this.root.getBoundingClientRect().top + window.scrollY + i * this.zone;
    window.scrollTo({ top: Math.round(top), behavior: reduced.matches ? 'auto' : 'smooth' });
  }

  onClick(e) {
    const a = e.target.closest ? e.target.closest('[data-nt-go]') : null;
    if (!a) return;
    e.preventDefault();
    this.goTo(parseInt(a.dataset.ntGo, 10) || 0);
  }

  onFocus(e) {
    const step = e.target.closest ? e.target.closest('[data-nt-step]') : null;
    const i = this.steps.indexOf(step);
    if (i >= 0 && !step.classList.contains('is-on')) this.goTo(i);
  }
}

function stacks(root) {
  root.querySelectorAll('[data-nt-stack]').forEach((el) => {
    if (instances.has(el)) return;
    if (reduced.matches) return; /* liste statique, voir le CSS */
    if (!el.querySelector('[data-nt-layer]')) return;
    instances.set(el, new Stack(el));
  });
}

/* ------------------------------------------------ 3. Filets sans couture */

function seamLines(root) {
  root.querySelectorAll('[data-nt-seam]').forEach((grid) => {
    if (grid.dataset.ntReady) return;
    grid.dataset.ntReady = '1';
    const svg = grid.querySelector('.nt-seam__lines');
    const path = svg && svg.querySelector('path');
    const img = grid.querySelector('.nt-seam__img');
    const bar = grid.querySelector('.nt-seam__bar');
    if (!path || !img || !bar) return;
    const desktop = window.matchMedia('(min-width: 990px)');

    const draw = () => {
      const gb = grid.getBoundingClientRect();
      if (!gb.width) return;
      const ib = img.getBoundingClientRect();
      const bb = bar.getBoundingClientRect();
      const d = desktop.matches;
      const anchor = ((d ? grid.dataset.anchorD : grid.dataset.anchorM) || '50,50').split(',').map(parseFloat);
      const x1 = ib.left - gb.left + (ib.width * anchor[0]) / 100;
      const y1 = ib.top - gb.top + (ib.height * anchor[1]) / 100;
      const x2 = bb.left - gb.left + (d ? bb.width / 2 : bb.width * 0.588);
      const y2 = d ? y1 : bb.top - gb.top;
      svg.setAttribute('viewBox', `0 0 ${gb.width.toFixed(1)} ${gb.height.toFixed(1)}`);
      path.setAttribute('d', `M${x1.toFixed(1)} ${y1.toFixed(1)}L${x2.toFixed(1)} ${y2.toFixed(1)}`);
      path.style.setProperty('--nt-path', String(Math.ceil(Math.hypot(x2 - x1, y2 - y1)) + 2));
    };

    draw();
    if (hasRO) new ResizeObserver(draw).observe(grid);
    else window.addEventListener('resize', draw);
    grid.addEventListener('transitionend', draw);
    const pic = img.querySelector('img');
    if (pic && !pic.complete) pic.addEventListener('load', draw);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(draw);
  });
}

/* ------------------------------------------------------------ démarrage */

function init(root) {
  document.documentElement.classList.add('nt-js', 'nt-ready');
  reveals(root);
  stacks(root);
  seamLines(root);
}

function start() {
  init(document);

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    instances.forEach((inst, el) => {
      if (e.target.contains(el)) {
        inst.destroy();
        instances.delete(el);
      }
    });
  });
  document.addEventListener('shopify:block:select', (e) => {
    const layer = e.target && e.target.closest ? e.target.closest('[data-nt-layer]') : null;
    if (!layer) return;
    const el = layer.closest('[data-nt-stack]');
    const inst = el && instances.get(el);
    if (inst) inst.goTo(inst.layers.indexOf(layer));
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
